<script setup>
/**
 * FontAwesomeIcon - Drop-in replacement for @fortawesome/vue-fontawesome
 * Works with Font Awesome CDN Kit for auto-subsetting
 *
 * Supports both syntaxes:
 * - Array: :icon="['fas', 'check']"
 * - String: icon="fa-solid fa-check"
 *
 * Toggle mode (for active/inactive states):
 * - :icon="['fal', 'home']" :active-icon="['fas', 'home']" :active="isActive"
 * - Renders both icons and toggles visibility to avoid CDN re-render issues
 *
 * Multi-state mode (for 3+ states like sort icons):
 * - :icons="['fa-solid fa-sort', 'fa-solid fa-sort-up', 'fa-solid fa-sort-down']" :active-index="0"
 * - Renders all icons and shows only the one at activeIndex
 */
import { computed } from 'vue'

const props = defineProps({
  icon: {
    type: [Array, String],
    default: null
  },
  activeIcon: {
    type: [Array, String],
    default: null
  },
  active: {
    type: Boolean,
    default: false
  },
  // Multi-state mode props
  icons: {
    type: Array,
    default: null
  },
  activeIndex: {
    type: Number,
    default: 0
  },
  spin: Boolean,
  pulse: Boolean,
  size: String,
  fixedWidth: Boolean,
  border: Boolean,
  inverse: Boolean,
  flip: String,
  rotation: [Number, String]
})

const prefixMap = {
  fas: 'fa-solid',
  fal: 'fa-light',
  fab: 'fa-brands',
  far: 'fa-regular',
  fad: 'fa-duotone',
  fadl: 'fa-duotone fa-light',
  'fa-solid': 'fa-solid',
  'fa-light': 'fa-light',
  'fa-brands': 'fa-brands',
  'fa-regular': 'fa-regular',
  'fa-duotone': 'fa-duotone'
}

// Helper to build class string from icon prop
const buildIconClass = (iconProp) => {
  if (!iconProp) return null

  const classes = []

  if (Array.isArray(iconProp)) {
    const [prefix, name] = iconProp
    classes.push(prefixMap[prefix] || prefix)
    classes.push(`fa-${name}`)
  } else if (typeof iconProp === 'string') {
    // String format: "fa-solid fa-check" - already in correct format
    classes.push(...iconProp.split(' '))
  }

  if (props.spin) classes.push('fa-spin')
  if (props.pulse) classes.push('fa-pulse')
  if (props.size) classes.push(`fa-${props.size}`)
  if (props.fixedWidth) classes.push('fa-fw')
  if (props.border) classes.push('fa-border')
  if (props.inverse) classes.push('fa-inverse')
  if (props.flip) classes.push(`fa-flip-${props.flip}`)
  if (props.rotation) classes.push(`fa-rotate-${props.rotation}`)

  return classes.join(' ')
}

const iconClass = computed(() => buildIconClass(props.icon))
const activeIconClass = computed(() => props.activeIcon ? buildIconClass(props.activeIcon) : null)
const hasToggleMode = computed(() => props.activeIcon !== null)
const hasMultiStateMode = computed(() => props.icons !== null && Array.isArray(props.icons) && props.icons.length > 0)

// Build classes for all icons in multi-state mode
const multiStateClasses = computed(() => {
  if (!hasMultiStateMode.value) return []
  return props.icons.map(icon => buildIconClass(icon))
})
</script>

<template>
  <!-- Multi-state mode: render all icons, show only activeIndex -->
  <span v-if="hasMultiStateMode" class="fa-icon-toggle">
    <span
      v-for="(iconCls, index) in multiStateClasses"
      :key="index"
      class="fa-icon-state"
      :class="{ 'is-active': index === activeIndex }"
    >
      <i :class="iconCls"></i>
    </span>
  </span>
  <!-- Toggle mode: render both icons, toggle visibility via wrapper -->
  <span v-else-if="hasToggleMode" class="fa-icon-toggle">
    <span class="fa-icon-state" :class="{ 'is-active': !active }">
      <i :class="iconClass"></i>
    </span>
    <span class="fa-icon-state" :class="{ 'is-active': active }">
      <i :class="activeIconClass"></i>
    </span>
  </span>
  <!-- Standard mode: single icon -->
  <i v-else-if="iconClass" :class="iconClass"></i>
</template>

<style>
.fa-icon-toggle {
  display: inline-flex;
  position: relative;
}

.fa-icon-state {
  display: inline-flex;
  opacity: 0;
  pointer-events: none;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.fa-icon-state.is-active {
  opacity: 1;
  pointer-events: auto;
}

.fa-icon-state:first-child {
  position: relative;
  top: auto;
  left: auto;
  transform: none;
}
</style>
